import { Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Icon } from '@iconify/react';

const faqs = [
  {
    question: 'What is TDEE?',
    answer: (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>TDEE (Total Daily Energy Expenditure)</strong> is the
          estimated number of calories you burn per day based on your Basal
          Metabolic Rate (BMR) and physical activity level.
        </Typography>
      </>
    ),
  },
  {
    question: 'How is BMR calculated?',
    answer: (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We use the <strong>Mifflin-St Jeor Equation</strong>:
        </Typography>
        <Box
          sx={{
            p: 2,
            backgroundColor: 'grey.50',
            borderRadius: 2,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            mb: 2,
          }}
        >
          For men: BMR = 10 × weight (kg) + 6.25 × height (cm) – 5 × age (y) + 5
          <br />
          For women: BMR = 10 × weight (kg) + 6.25 × height (cm) – 5 × age (y) – 161
        </Box>
        <Typography variant="body1">
          Source:{' '}
          <Box
            component="a"
            href="https://www.cdc.gov/bmi/about/index.html"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Mifflin MD, St Jeor ST
          </Box>
        </Typography>
      </>
    ),
  },
  {
    question: 'How is TDEE calculated?',
    answer: (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We multiply your BMR by your activity factor:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Sedentary (little or no exercise): × 1.2
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Light activity (1–3 days/week): × 1.375
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Moderate activity (3–5 days/week): × 1.55
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Very active (6–7 days/week): × 1.725
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Extra active (physical job or twice daily training): × 1.9
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
          Sources:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>CDC</strong>:{' '}
            <Box
              component="a"
              href="https://www.cdc.gov/healthy-weight-growth/about/tips-for-balancing-food-activity.html"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
            >
              Understanding Calories and Energy Expenditure
            </Box>
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>NIH</strong>:{' '}
            <Box
              component="a"
              href="https://www.niddk.nih.gov/health-information/weight-management/body-weight-planner"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
            >
              Body Weight Planner and TDEE Calculation
            </Box>
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>USDA</strong>:{' '}
            <Box
              component="a"
              href="https://www.myplate.gov/myplate-plan"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
            >
              MyPlate Plan Based on Activity Level
            </Box>
          </Typography>
          <Typography component="li" variant="body1">
            <strong>PubMed</strong>:{' '}
            <Box
              component="a"
              href="https://pubmed.ncbi.nlm.nih.gov/12221202/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
            >
              Total Energy Expenditure: Components and Assessment Methods
            </Box>
          </Typography>
        </Box>
      </>
    ),
  },
  {
    question: 'What is BMI and how is it calculated?',
    answer: (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>BMI (Body Mass Index)</strong> is a simple index of
          weight-for-height used to classify underweight, normal weight,
          overweight, and obesity.
        </Typography>
        <Box
          sx={{
            p: 2,
            backgroundColor: 'grey.50',
            borderRadius: 2,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            mb: 2,
          }}
        >
          BMI = weight (kg) / (height (m) × height (m))
        </Box>
        <Typography variant="body1" sx={{ mb: 1 }}>
          According to the World Health Organization (WHO):
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            BMI &lt; 18.5 — Underweight
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            BMI 18.5–24.9 — Normal weight
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            BMI 25–29.9 — Overweight
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            BMI ≥ 30 — Obesity
          </Typography>
        </Box>
        <Typography variant="body1">
          Source:{' '}
          <Box
            component="a"
            href="https://www.cdc.gov/healthyweight/assessing/bmi/index.html"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            CDC – About Adult BMI
          </Box>
        </Typography>
      </>
    ),
  },
  {
    question: 'Why does Calorium use HealthKit?',
    answer: (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Calorium integrates with <strong>Apple HealthKit</strong> to provide
          more accurate and personalized calorie tracking. HealthKit collects
          real-time data from your Apple Watch and other compatible devices,
          including:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Active energy burned during workouts
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Steps taken and walking distance
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Heart rate and other fitness metrics
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          By syncing with HealthKit, Calorium can automatically add calories
          burned to your daily total and include this data in your global health
          journal. This helps you stay on track with your goals without needing
          to enter everything manually.
        </Typography>
        <Typography variant="body1">
          You remain in full control of your data — Calorium only accesses the
          data you explicitly allow.
        </Typography>
      </>
    ),
  },
  {
    question: 'How can I improve the accuracy when scanning a meal?',
    answer: (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          To improve food recognition accuracy when using the camera in
          Calorium, follow these tips:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            📸 Hold your phone directly above the meal at a 90° angle
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            📏 Maintain a consistent distance — about 50 cm (20 inches) from the
            plate
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            💡 Make sure the lighting is bright and even (avoid shadows)
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            🍽 Place the food on a clear, non-distracting background (like a
            plain plate)
          </Typography>
        </Box>
        <Typography variant="body1">
          These small adjustments significantly increase the precision of
          AI-based food recognition.
        </Typography>
      </>
    ),
  },
  {
    question: 'Is it safe to use these formulas without a doctor?',
    answer: (
      <Typography variant="body1">
        These calculations are general estimates and should not replace
        professional medical advice. If you have any medical conditions or
        concerns, please consult a healthcare provider.
      </Typography>
    ),
  },
];

export const FAQPage = () => {
  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="md">
        <Typography
          variant="h1"
          sx={{
            mb: 6,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
          }}
        >
          FAQ
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                  mb: 2,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon icon="solar:alt-arrow-down-bold" width={24} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    my: 2,
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>{faq.answer}</AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
};